/**
 * SecurityHaloCard — unifies the nudge flow into one holistic
 * Settings surface. Three sections:
 *
 *   1. Counters strip: Sent / Worked / Pending
 *   2. Peers who are currently slipping or inactive, each with a
 *      one-tap "Nudge" button that sends the standard encrypted
 *      rotation-reminder through the user's 1:1 conversation with
 *      that peer (or starts one if it doesn't exist).
 *   3. Empty state when no peers need a nudge — positive
 *      reinforcement, not a scold.
 *
 * Uses existing infra end-to-end — no new backend calls beyond the
 * already-cached `/users/{id}/key-history` reads that the rotation
 * ring system warms on Dashboard mount. Duress-safe by virtue of
 * every sub-hook bailing on sessionStorage['gs_duress_mode'].
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Send, ChevronRight, Share2, Loader2, PhoneCall } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import {
  useRotationHealthDetail,
  HEALTH_DOT_CLASS,
} from '../hooks/useRotationHealth';
import {
  nudgesSentTotal,
  nudgesWorkedCount,
  pendingNudgeCount,
  recordNudge,
} from '../utils/nudgeFeedback';
import {
  mySasVerifiedCount90d,
  peerAttestedMeCount90d,
} from '../utils/voiceSAS';
import { generateHaloSnapshot } from '../utils/haloSnapshot';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NUDGE_BODY =
  "Friendly heads-up: your keys haven't rotated in a while. " +
  "Pop into Settings → Privacy → Rotate now to refresh them. " +
  "Takes five seconds; keeps our thread healthy. 🛡";

// One row per slipping/inactive peer. Extracted so that each row
// gets its own `useRotationHealthDetail` call (hooks can't live in
// a `.map()` callback).
const PeerRow = ({ peer, conversations, userId, onSent }) => {
  const detail = useRotationHealthDetail(peer.id);
  const { encryptMessage } = useAuth();
  const [sending, setSending] = useState(false);

  // This row only participates in the card when the peer is
  // slipping or inactive — rendering the null/healthy case is the
  // parent's job so we can maintain a stable count.
  if (!detail || (detail.health !== 'slipping' && detail.health !== 'inactive')) {
    return null;
  }

  const send = async () => {
    if (sending) return;
    setSending(true);
    try {
      // Find-or-create the 1:1 conversation. Fast path: a cached
      // conversation with exactly [user, peer] as participants.
      // Slow path: hit `POST /api/conversations` so the server
      // returns the existing row (it's idempotent).
      let conv = (conversations || []).find(
        (c) => c.type !== 'group'
          && (c.participants || []).includes(peer.id)
          && (c.participants || []).includes(userId)
      );
      if (!conv) {
        const res = await axios.post(`${API}/conversations`, { participant_id: peer.id });
        conv = res.data;
      }

      // Fetch participant public keys + PQ bundles inline. Mirrors
      // what ChatView does; deliberately not imported to avoid
      // coupling the halo card to ChatView's component tree.
      const recipientKeys = {};
      const pqBundles = {};
      for (const participantId of conv.participants) {
        if (participantId === userId) continue;
        try {
          const userRes = await axios.get(`${API}/users/${participantId}`);
          if (userRes.data?.public_key) {
            recipientKeys[participantId] = JSON.parse(userRes.data.public_key);
          }
        } catch { /* missing key — skip, encryptMessage drops that recipient */ }
        try {
          const bundleRes = await axios.get(`${API}/users/${participantId}/pq-bundle`);
          if (bundleRes.data?.kyber_pub) pqBundles[participantId] = bundleRes.data;
        } catch { /* peer has no PQ bundle — classical-only recipient */ }
      }

      const { encrypted_content, encrypted_keys, encrypted_keys_pq } =
        await encryptMessage(NUDGE_BODY, recipientKeys, pqBundles);
      const msgRes = await axios.post(`${API}/messages`, {
        conversation_id: conv.id,
        encrypted_content,
        encrypted_keys,
        encrypted_keys_pq,
      });

      recordNudge({
        peerId: peer.id,
        peerUsername: peer.username,
        peerLatestAtSent: detail.latestAt || 0,
      });
      toast.success(`Nudge sent to ${peer.username}`);
      onSent?.(peer.id, msgRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send nudge');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="flex items-center gap-3 py-2 px-1"
      data-testid={`halo-peer-${peer.id}`}
      data-peer-health={detail.health}
    >
      <span
        className={`w-2 h-2 rounded-full shrink-0 ${HEALTH_DOT_CLASS[detail.health]}`}
        aria-hidden
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white truncate">{peer.username}</div>
        <div className="text-[11px] text-slate-500">
          {detail.days === 1 ? '1 day' : `${detail.days} days`} since rotation
        </div>
      </div>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={send}
        disabled={sending}
        className="text-xs h-7 px-2"
        data-testid={`halo-nudge-${peer.id}`}
      >
        <Send className="w-3 h-3 mr-1" />
        {sending ? 'Sending…' : 'Nudge'}
      </Button>
    </div>
  );
};

/**
 * Parent card. Needs `contacts` + `conversations` to enumerate
 * nudge candidates; these already exist in Dashboard state so we
 * thread them through SettingsDialog as new props.
 */
export const SecurityHaloCard = ({ contacts = [], conversations = [] }) => {
  const { user } = useAuth();

  const [sent, setSent] = useState(nudgesSentTotal());
  const [worked, setWorked] = useState(nudgesWorkedCount());
  const [pending, setPending] = useState(pendingNudgeCount());
  // Voice-security posture counters — complement the nudge ledger
  // with a second ritual (SAS verification) so users can see both
  // retention-flywheel metrics in one glance.
  const [sasByMe, setSasByMe] = useState(mySasVerifiedCount90d());
  const [sasByPeer, setSasByPeer] = useState(peerAttestedMeCount90d());

  // Refresh counters whenever the user sends another nudge from
  // THIS card (so the strip updates live) — done by the `onSent`
  // callback below. We also re-read on mount so counters surfaced
  // elsewhere in the app (e.g. ChatView's inline prompt) land here
  // when the dialog opens.
  useEffect(() => {
    setSent(nudgesSentTotal());
    setWorked(nudgesWorkedCount());
    setPending(pendingNudgeCount());
    setSasByMe(mySasVerifiedCount90d());
    setSasByPeer(peerAttestedMeCount90d());
  }, []);

  // Track which peers have already been nudged from THIS mount so
  // we can hide their rows immediately without a second fetch.
  const [justNudged, setJustNudged] = useState(() => new Set());

  const onSent = (peerId) => {
    setSent(nudgesSentTotal());
    setWorked(nudgesWorkedCount());
    setPending(pendingNudgeCount());
    setJustNudged((s) => new Set(s).add(peerId));
  };

  // Share snapshot: fetch the caller's own key-history (one
  // request — no peer data, no PII), render a 1200×630 PNG, and
  // hand it to the user via the native share sheet when the
  // browser supports files, else a plain download link.
  const [sharing, setSharing] = useState(false);
  const onShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      let timestamps = [];
      try {
        const r = await axios.get(`${API}/users/me/key-history`);
        timestamps = (r.data?.entries || [])
          .map((e) => Date.parse(e.created_at))
          .filter((n) => !Number.isNaN(n));
      } catch {
        // Swallow — the snapshot has a graceful "No rotation
        // history yet" fallback so we still produce a valid image.
      }
      const blob = await generateHaloSnapshot({
        sent, worked, pending,
        sasVerifiedByPeers: sasByPeer,
        rotationTimestamps: timestamps,
      });
      if (!blob) throw new Error('Canvas render failed');
      const file = new File(
        [blob],
        'government-shield-halo.png',
        { type: 'image/png' }
      );

      // Prefer the native share sheet on mobile / Safari. We
      // feature-detect with `canShare({files})` because some
      // desktops advertise share() but reject file payloads.
      const canShareFiles = !!(
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      );
      if (canShareFiles) {
        try {
          await navigator.share({
            files: [file],
            title: 'My Government Shield security halo',
            text: 'Keys I rotate. Contacts I nudge.',
          });
          toast.success('Shared!');
          return;
        } catch (e) {
          // User cancelled share sheet → not an error.
          if (e?.name === 'AbortError') return;
          // Fall through to download path.
        }
      }

      // Download fallback. Build an ephemeral anchor + click it,
      // then revoke the URL right after so we don't hold a blob
      // reference in memory.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'government-shield-halo.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Snapshot downloaded');
    } catch (e) {
      toast.error(e?.message || 'Could not build snapshot');
    } finally {
      setSharing(false);
    }
  };

  // Only consider real contacts — decoys and self are excluded.
  const candidates = useMemo(() => {
    if (!Array.isArray(contacts) || !user?.id) return [];
    return contacts.filter(
      (c) => c.id && c.id !== user.id && !justNudged.has(c.id)
    );
  }, [contacts, user?.id, justNudged]);

  return (
    <div className="space-y-2" data-testid="security-halo-card">
      <label className="text-sm text-slate-400 flex items-center gap-2">
        <Sparkles className="w-4 h-4" />
        Your security halo
      </label>

      <div className="p-4 rounded-lg bg-secondary/30 space-y-4">
        <p className="text-xs text-slate-500">
          Every nudge you send trains another contact to rotate
          their keys. Healthy peers get emerald halos; slipping
          peers turn amber; inactive ones, rose. Keeps the thread
          fresh and tightens the whole network's crypto hygiene.
        </p>

        {/* Counters strip */}
        <div
          className="grid grid-cols-3 gap-2 text-center"
          data-testid="halo-counters"
        >
          <div className="rounded-md bg-background/40 border border-border/60 p-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Sent</div>
            <div className="text-lg font-mono text-slate-100">{sent}</div>
          </div>
          <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-2">
            <div className="text-[10px] uppercase tracking-wider text-emerald-400/80">Worked</div>
            <div className="text-lg font-mono text-emerald-300">{worked}</div>
          </div>
          <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-2">
            <div className="text-[10px] uppercase tracking-wider text-amber-400/80">Pending</div>
            <div className="text-lg font-mono text-amber-200">{pending}</div>
          </div>
        </div>

        {/* Voice-security posture — distinct-peer counts of 90d
            SAS attestations in both directions. Renders only once
            either counter is non-zero so a user who has never
            verified a call doesn't see an empty "0 / 0" strip
            that would feel like a scold. */}
        {(sasByMe > 0 || sasByPeer > 0) && (
          <div
            className="grid grid-cols-2 gap-2 text-center"
            data-testid="halo-voice-counters"
          >
            <div className="rounded-md bg-primary/10 border border-primary/30 p-2">
              <div className="text-[10px] uppercase tracking-wider text-primary/80 flex items-center justify-center gap-1">
                <PhoneCall className="w-3 h-3" /> Verified by you
              </div>
              <div
                className="text-lg font-mono text-primary"
                data-testid="halo-voice-by-me"
              >{sasByMe}</div>
            </div>
            <div className="rounded-md bg-violet-500/10 border border-violet-500/30 p-2">
              <div className="text-[10px] uppercase tracking-wider text-violet-300/80">
                Verified by peers
              </div>
              <div
                className="text-lg font-mono text-violet-200"
                data-testid="halo-voice-by-peer"
              >{sasByPeer}</div>
            </div>
          </div>
        )}

        {/* Share snapshot: zero-PII, zero-backend. Opt-in social
            proof that makes cryptographic hygiene visible. */}
        <div className="flex items-center justify-between gap-2 pt-1">
          <p className="text-[11px] text-slate-500 leading-snug flex-1">
            Share a zero-PII snapshot — just the counters + your rotation
            rhythm. No names, no peers.
          </p>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={onShare}
            disabled={sharing}
            className="text-xs h-7 px-2 shrink-0"
            data-testid="halo-share-btn"
          >
            {sharing ? (
              <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Building…</>
            ) : (
              <><Share2 className="w-3 h-3 mr-1" /> Share halo snapshot</>
            )}
          </Button>
        </div>

        {/* Peers needing a nudge — null-tolerant because each row's
            detail hook may return slipping/inactive/healthy/null;
            rows render only when truly in need. */}
        <div
          className="space-y-1 border-t border-border/60 pt-3"
          data-testid="halo-peer-list"
        >
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center justify-between">
            <span>Peers to nudge</span>
            <span className="flex items-center gap-0.5 text-slate-600">
              <ChevronRight className="w-3 h-3" />
              one tap each
            </span>
          </div>
          {candidates.map((peer) => (
            <PeerRow
              key={peer.id}
              peer={peer}
              conversations={conversations}
              userId={user?.id}
              onSent={onSent}
            />
          ))}
          {/* Empty state: shown when we have contacts but none of
              them resolve to slipping/inactive. Crude but sufficient
              — the row components are the source of truth for the
              "needs nudging" bucket; we can't enumerate from here. */}
          {candidates.length === 0 && (
            <div
              className="text-xs text-slate-500 py-1"
              data-testid="halo-peer-empty"
            >
              No contacts yet — add some to start building your halo.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurityHaloCard;
